import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { Outlet, useLocation, useMatch } from 'react-router-dom';
import RequireRole from '../../auth/RequireRole';
import { useAuth } from '../../auth/AuthProvider';
import { admin, member } from '../../lib/api';
import type { Team, UserProfile } from '../../types';
import { Avatar } from '../../ui/avatar';
import {
  Navbar,
  NavbarItem,
  NavbarLabel,
  NavbarSection,
  NavbarSpacer,
} from '../../ui/navbar';
import {
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarHeader,
  SidebarHeading,
  SidebarItem,
  SidebarLabel,
  SidebarSection,
  SidebarSpacer,
} from '../../ui/sidebar';
import { SidebarLayout } from '../../ui/sidebar-layout';
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '../../ui/dropdown';
import Logo from '../../components/Logo';

// Single source of truth for the team currently in scope inside /admin/*.
// The AdminLayout fetches it once per teamId; sidebar + nested routes read it.

interface CurrentTeamState {
  teamId: string | undefined;
  team: Team | undefined;
  loading: boolean;
  error: string | undefined;
  refresh: () => Promise<void>;
}

const noopRefresh = async () => {
  /* no-op default */
};

const CurrentTeamContext = createContext<CurrentTeamState>({
  teamId: undefined,
  team: undefined,
  loading: false,
  error: undefined,
  refresh: noopRefresh,
});

export function useCurrentTeam(): CurrentTeamState {
  return useContext(CurrentTeamContext);
}

function CurrentTeamProvider({ children }: { children: ReactNode }) {
  const teamMatch = useMatch('/admin/teams/:teamId/*');
  const teamId = teamMatch?.params.teamId;

  const [state, setState] = useState<Omit<CurrentTeamState, 'refresh'>>({
    teamId,
    team: undefined,
    loading: false,
    error: undefined,
  });
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!teamId) {
      setState({ teamId: undefined, team: undefined, loading: false, error: undefined });
      return;
    }
    let cancelled = false;
    setState({ teamId, team: undefined, loading: true, error: undefined });
    void (async () => {
      try {
        const t = await admin.getTeam(teamId);
        if (!cancelled) {
          setState({ teamId, team: t, loading: false, error: undefined });
        }
      } catch (err) {
        if (!cancelled) {
          setState({
            teamId,
            team: undefined,
            loading: false,
            error: err instanceof Error ? err.message : String(err),
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId, version]);

  const refresh = async () => {
    setVersion((v) => v + 1);
  };

  return (
    <CurrentTeamContext.Provider value={{ ...state, refresh }}>
      {children}
    </CurrentTeamContext.Provider>
  );
}

function AdminSidebar() {
  const location = useLocation();
  const { teamId, team } = useCurrentTeam();
  const { user, email, signOut } = useAuth();
  const [profile, setProfile] = useState<UserProfile>();

  const path = location.pathname;
  const isOverview = path === '/admin';
  const isTeamsList = path === '/admin/teams';

  useEffect(() => {
    if (!user) {
      setProfile(undefined);
      return;
    }
    let cancelled = false;
    void member
      .profile()
      .then((next) => {
        if (!cancelled) setProfile(next);
      })
      .catch(() => {
        if (!cancelled) setProfile(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const fullName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ').trim();
  const accountName = profile?.displayName || fullName;
  const initials =
    (accountName || email)
      ?.split('@')[0]
      ?.split(/[._\-\s]+/)
      .map((s) => s[0]?.toUpperCase() ?? '')
      .join('')
      .slice(0, 2) ?? '?';
  const accountLabel = accountName || email || 'Signed in';

  return (
    <Sidebar>
      <SidebarHeader>
        <SidebarItem href="/" aria-label="Public site">
          <SidebarLabel>
            <Logo />
          </SidebarLabel>
        </SidebarItem>
      </SidebarHeader>

      <SidebarBody>
        <SidebarSection>
          <SidebarHeading>Platform</SidebarHeading>
          <SidebarItem href="/admin" current={isOverview}>
            <SidebarLabel>Overview</SidebarLabel>
          </SidebarItem>
          <SidebarItem href="/admin/teams" current={isTeamsList}>
            <SidebarLabel>Teams</SidebarLabel>
          </SidebarItem>
        </SidebarSection>

        {teamId ? (
          <SidebarSection>
            <SidebarHeading>{team?.name ?? teamId}</SidebarHeading>
            <SidebarItem
              href={`/admin/teams/${teamId}/games`}
              current={path.startsWith(`/admin/teams/${teamId}/games`)}
            >
              <SidebarLabel>Games</SidebarLabel>
            </SidebarItem>
            <SidebarItem
              href={`/admin/teams/${teamId}/calendar`}
              current={path.startsWith(`/admin/teams/${teamId}/calendar`)}
            >
              <SidebarLabel>Calendar</SidebarLabel>
            </SidebarItem>
            <SidebarItem
              href={`/admin/teams/${teamId}`}
              current={
                path === `/admin/teams/${teamId}` ||
                path === `/admin/teams/${teamId}/` ||
                path.startsWith(`/admin/teams/${teamId}/players`)
              }
            >
              <SidebarLabel>Roster</SidebarLabel>
            </SidebarItem>
            <SidebarItem
              href={`/admin/teams/${teamId}/announcements`}
              current={path.startsWith(`/admin/teams/${teamId}/announcements`)}
            >
              <SidebarLabel>Announcements</SidebarLabel>
            </SidebarItem>
            <SidebarItem
              href={`/admin/teams/${teamId}/schedule`}
              current={path.startsWith(`/admin/teams/${teamId}/schedule`)}
            >
              <SidebarLabel>Seasons &amp; Tournaments</SidebarLabel>
            </SidebarItem>
            <SidebarItem
              href={`/admin/teams/${teamId}/opponents`}
              current={path.startsWith(`/admin/teams/${teamId}/opponents`)}
            >
              <SidebarLabel>Opponents</SidebarLabel>
            </SidebarItem>
            <SidebarItem
              href={`/admin/teams/${teamId}/members`}
              current={path.startsWith(`/admin/teams/${teamId}/members`)}
            >
              <SidebarLabel>Members</SidebarLabel>
            </SidebarItem>
            <SidebarItem
              href={`/admin/teams/${teamId}/fans`}
              current={path.startsWith(`/admin/teams/${teamId}/fans`)}
            >
              <SidebarLabel>Fans</SidebarLabel>
            </SidebarItem>
            <SidebarItem
              href={`/admin/teams/${teamId}/stats`}
              current={path.startsWith(`/admin/teams/${teamId}/stats`)}
            >
              <SidebarLabel>Stats</SidebarLabel>
            </SidebarItem>
            <SidebarItem
              href={`/admin/teams/${teamId}/clips`}
              current={path.startsWith(`/admin/teams/${teamId}/clips`)}
            >
              <SidebarLabel>Clips</SidebarLabel>
            </SidebarItem>
            <SidebarItem
              href={`/admin/teams/${teamId}/details`}
              current={path.startsWith(`/admin/teams/${teamId}/details`)}
            >
              <SidebarLabel>Team details</SidebarLabel>
            </SidebarItem>
          </SidebarSection>
        ) : null}

        <SidebarSpacer />

        <SidebarSection>
          <SidebarItem href="/me">
            <SidebarLabel className="text-zinc-500">My BaseballSTRM</SidebarLabel>
          </SidebarItem>
          <SidebarItem href="/teams">
            <SidebarLabel className="text-zinc-500">View as fan →</SidebarLabel>
          </SidebarItem>
        </SidebarSection>
      </SidebarBody>

      <SidebarFooter>
        <Dropdown>
          <DropdownButton as={SidebarItem}>
            <Avatar
              src={profile?.avatarUrl}
              initials={initials}
              className="size-8"
              square
              alt={accountLabel}
            />
            <SidebarLabel>{accountLabel}</SidebarLabel>
          </DropdownButton>
          <DropdownMenu className="min-w-64" anchor="top start">
            {accountName && email ? (
              <DropdownItem disabled>
                <DropdownLabel>{email}</DropdownLabel>
              </DropdownItem>
            ) : null}
            <DropdownItem href="/admin/profile">
              <DropdownLabel>Profile</DropdownLabel>
            </DropdownItem>
            <DropdownItem onClick={() => void signOut()}>
              <DropdownLabel>Sign out</DropdownLabel>
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </SidebarFooter>
    </Sidebar>
  );
}

function AdminMobileNavbar() {
  return (
    <Navbar>
      <NavbarSpacer />
      <NavbarSection>
        <NavbarItem href="/admin">
          <NavbarLabel>
            <Logo />
          </NavbarLabel>
        </NavbarItem>
      </NavbarSection>
    </Navbar>
  );
}

export default function AdminLayout() {
  return (
    <RequireRole roles={['admin', 'team-admin', 'scorekeeper']}>
      <CurrentTeamProvider>
        <SidebarLayout sidebar={<AdminSidebar />} navbar={<AdminMobileNavbar />}>
          <Outlet />
        </SidebarLayout>
      </CurrentTeamProvider>
    </RequireRole>
  );
}
